import React, { useEffect, useState, useMemo } from 'react';
import UserAvatar from '../common/UserAvatar';
import './FreelancerProfile.css';
import { auth } from '../../firebase';

export default function FreelancerProfile({ currentUser, profile, isRTL }) {
  const displayName = profile?.displayName || currentUser?.displayName || (currentUser?.email?.split('@')[0]) || 'Freelancer';
  const [level, setLevel] = useState(1);
  const [loadingLevel, setLoadingLevel] = useState(true);
  const [xp, setXp] = useState(0);
  const [jobsCompleted, setJobsCompleted] = useState(0);
  const [rating, setRating] = useState(3);
  const aboutText = typeof profile?.about === 'string' ? profile.about.trim() : '';
  const freelancerCategory =
    typeof profile?.freelancerCategory === 'string'
      ? profile.freelancerCategory.trim()
      : '';

  const getApiBaseUrl = useMemo(() => {
    return () => {
      const envUrl = process.env.REACT_APP_API_URL;
      if (!envUrl) {
        return process.env.NODE_ENV === 'production' ? '' : 'https://flask-router-340481947694.europe-west1.run.app';
      }
      if (envUrl.includes('your-cloud-run-url') || envUrl.includes('placeholder')) {
        return process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8081';
      }
      if (envUrl.includes('run.app')) {
        return envUrl;
      }
      if (process.env.NODE_ENV === 'production') {
        return envUrl;
      }
      return 'http://localhost:8081';
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    async function fetchLevel() {
      try {
        const user = auth.currentUser || currentUser;
        if (!user) return;
        const token = await user.getIdToken();
        const res = await fetch(`${getApiBaseUrl()}/api/freelancer/level`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) throw new Error('Failed to fetch level');
        const data = await res.json();
        if (mounted) {
          if (typeof data.level === 'number') {
            setLevel(data.level);
          }
          if (typeof data.xp === 'number') {
            setXp(data.xp);
          }
          if (typeof data.jobsCompleted === 'number') {
            setJobsCompleted(data.jobsCompleted);
          }
          if (typeof data.rating === 'number') {
            setRating(data.rating);
          }
        }
      } catch (e) {
        // keep default
        console.error('Failed loading freelancer level', e);
      } finally {
        if (mounted) setLoadingLevel(false);
      }
    }
    fetchLevel();
    return () => { mounted = false; };
  }, [currentUser, getApiBaseUrl]);
  const levelProgress = { currentXp: xp, maxXp: 3000 };

  const fullWidth = isRTL ? 'rtl' : '';
  const progressPercent = Math.min(100, Math.round((levelProgress.currentXp / levelProgress.maxXp) * 100));

  return (
    <div className={`freelancer-profile ${fullWidth}`} dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="freelancer-grid">
        <div className="freelancer-main-card">
          <div className="freelancer-card-header">
            <div className="freelancer-user">
              <UserAvatar user={currentUser} size="xl" />
              <div className="freelancer-user-info">
                <div className="freelancer-user-top">
                  <h2 className="freelancer-name">{displayName}</h2>
                  <span className="freelancer-badge">Elite</span>
                </div>
                {freelancerCategory && (
                  <div className="freelancer-category">{freelancerCategory}</div>
                )}
                <div className="freelancer-inline-stats">
                  <span>⚡ Level {loadingLevel ? '...' : level}</span>
                  <span>📈 {jobsCompleted} Jobs</span>
                  <span>⭐ {Number.isFinite(rating) ? rating.toFixed(1) : '3.0'}</span>
                </div>
              </div>
            </div>
          </div>
          <div className="freelancer-progress">
            <div className="freelancer-progress-label">Level Progress</div>
            <div className="freelancer-progress-bar">
              <div className="freelancer-progress-fill" style={{ width: `${progressPercent}%` }} />
            </div>
            <div className="freelancer-progress-value">
              {levelProgress.currentXp} / {levelProgress.maxXp} XP
            </div>
          </div>

          <div className="freelancer-section">
            <h3>About</h3>
            <p>
              {aboutText || 'This freelancer has not added an about section yet.'}
            </p>
          </div>

          <div className="freelancer-section">
            <h3>Contact Information</h3>
            <div className="freelancer-contact">
              <div className="freelancer-contact-item">Email: {currentUser?.email}</div>
              <div className="freelancer-contact-item">Phone: {profile?.phone || '—'}</div>
              <div className="freelancer-contact-item">📍 {profile?.location || '—'}</div>
            </div>
          </div>


        </div>

        <aside className="freelancer-side-card">
          <div className="freelancer-quick-title">Quick Stats</div>
          <div className="freelancer-quick-row">
            <div className="freelancer-quick-label">Current Streak</div>
            <div className="freelancer-quick-value">{(profile?.streakDays ?? 0)} days</div>
          </div>
          <div className="freelancer-quick-row">
            <div className="freelancer-quick-label">Total Jobs</div>
            <div className="freelancer-quick-value">{jobsCompleted}</div>
          </div>
          <div className="freelancer-quick-row">
            <div className="freelancer-quick-label">Rating</div>
            <div className="freelancer-quick-value">{Number.isFinite(rating) ? rating.toFixed(1) : '3.0'} ★</div>
          </div>
        </aside>
      </div>
    </div>
  );
}


